import React from 'react';

import { SupportButton } from '@opendoor/cinderblocks/buy/SupportCard';
import { Flex, Loader, ListItem, CardListItem } from '@opendoor/bricks/core';

import { Section } from 'shared/components/Section';
import { useContactUsModalContext } from 'sellDirect/helpers/ContactUsModalProvider';
import {
  useSupportContacts,
  ISupportContact,
  SupportContacts,
} from 'sellDirect/helpers/useSupportContacts';

export interface SupportSectionProps {
  /** List of contacts you want to display in the section */
  contactTypes: (keyof SupportContacts)[];
}

/** Shared component that renders the section to show the support contacts */
export const SupportSection: React.FC<SupportSectionProps> = ({ contactTypes }) => {
  const { openContactUsModal } = useContactUsModalContext();
  const { loadingContacts, ...contacts } = useSupportContacts();

  const contactsToShow = contactTypes
    .map((contactType) => contacts[contactType])
    .filter((contact): contact is ISupportContact => !!contact);

  return (
    <Section data-testid="support-section" title="Support" mt="10">
      <Flex as="ul" flexDirection={['column', null, 'row']} gridGap="5">
        {loadingContacts ? (
          <>
            <ListItem>
              <CardListItem p="5">
                <Loader padding="less" />
              </CardListItem>
            </ListItem>
            <ListItem>
              <CardListItem p="5">
                <Loader padding="less" />
              </CardListItem>
            </ListItem>
          </>
        ) : (
          contactsToShow.map((contact, index) => (
            <ListItem
              key={`${contact.name}-${index}`}
              flex="1"
              role="group"
              aria-label="Support card"
            >
              <SupportButton
                data-testid={`support-card-${index}`}
                contact={contact}
                onClick={() => openContactUsModal(contact)}
              />
            </ListItem>
          ))
        )}
      </Flex>
    </Section>
  );
};
